/**
 * Admin > Analytics & Insights (Phase 1) -- overview cards, per-user
 * activity, feature adoption, sessions, API health, and an events
 * breakdown, all scoped to a date range (Today / 7 Days / 30 Days /
 * Custom). Read-only; every call goes through `analyticsService`, which
 * hits the ADMIN-gated `/api/v1/admin/analytics/*` endpoints (see
 * `app.api.v1.admin_analytics` on the backend -- identical role-gating to
 * every other Admin page).
 *
 * All timestamps from the API are UTC; this page converts to the
 * viewer's local timezone only for display (`toLocaleString()`), never
 * for storage or requests.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import analyticsService from "@/services/analyticsService";
import type {
  AnalyticsApiHealthResponse,
  AnalyticsDateRangePreset,
  AnalyticsEventsResponse,
  AnalyticsFeaturesResponse,
  AnalyticsOverviewResponse,
  AnalyticsSessionsResponse,
  AnalyticsUsersResponse,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

function formatLocal(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function OverviewCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Grid item xs={6} sm={4} md={3} lg={1.5}>
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {value}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

export function AnalyticsInsightsPage() {
  const [rangePreset, setRangePreset] = useState<AnalyticsDateRangePreset>("7d");
  const [customFrom, setCustomFrom] = useState(() => new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<AnalyticsOverviewResponse | null>(null);
  const [users, setUsers] = useState<AnalyticsUsersResponse | null>(null);
  const [events, setEvents] = useState<AnalyticsEventsResponse | null>(null);
  const [features, setFeatures] = useState<AnalyticsFeaturesResponse | null>(null);
  const [sessions, setSessions] = useState<AnalyticsSessionsResponse | null>(null);
  const [apiHealth, setApiHealth] = useState<AnalyticsApiHealthResponse | null>(null);

  const params = useMemo(() => {
    if (rangePreset === "custom") {
      return { range: "custom" as const, from_date: customFrom, to_date: customTo };
    }
    return { range: rangePreset };
  }, [rangePreset, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, usersRes, eventsRes, featuresRes, sessionsRes, apiHealthRes] = await Promise.all([
        analyticsService.getOverview(params),
        analyticsService.getUsers(params),
        analyticsService.getEvents(params),
        analyticsService.getFeatures(params),
        analyticsService.getSessions(params),
        analyticsService.getApiHealth(params),
      ]);
      setOverview(overviewRes);
      setUsers(usersRes);
      setEvents(eventsRes);
      setFeatures(featuresRes);
      setSessions(sessionsRes);
      setApiHealth(apiHealthRes);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const featureChartData = useMemo(
    () => ({
      labels: features?.items.map((f) => f.feature_name) ?? [],
      datasets: [
        {
          label: "Unique users",
          data: features?.items.map((f) => f.unique_users) ?? [],
          backgroundColor: "#1E3A8A",
        },
      ],
    }),
    [features]
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h4" fontWeight={700}>
          Analytics & Insights
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <ToggleButtonGroup
            size="small"
            value={rangePreset}
            exclusive
            onChange={(_e, value) => {
              if (value) setRangePreset(value as AnalyticsDateRangePreset);
            }}
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="7d">7 Days</ToggleButton>
            <ToggleButton value="30d">30 Days</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>
          {rangePreset === "custom" && (
            <>
              <TextField
                label="From"
                type="date"
                size="small"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !overview ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <OverviewCard label="Total Users" value={overview?.total_users ?? "—"} />
            <OverviewCard label="New Users" value={overview?.new_users ?? "—"} />
            <OverviewCard label="DAU" value={overview?.dau ?? "—"} />
            <OverviewCard label="WAU" value={overview?.wau ?? "—"} />
            <OverviewCard label="MAU" value={overview?.mau ?? "—"} />
            <OverviewCard label="Active Sessions" value={overview?.active_sessions ?? "—"} />
            <OverviewCard label="Total Sessions" value={overview?.total_sessions ?? "—"} />
            <OverviewCard label="Avg Session" value={formatDuration(overview?.avg_session_duration_seconds)} />
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    User Activity
                  </Typography>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell align="right">Logins</TableCell>
                          <TableCell align="right">Sessions</TableCell>
                          <TableCell>Last Active</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(users?.items ?? []).map((row) => (
                          <TableRow key={row.user_id}>
                            <TableCell>
                              <Typography variant="body2">{row.full_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.email}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{row.login_count}</TableCell>
                            <TableCell align="right">{row.session_count}</TableCell>
                            <TableCell>{formatLocal(row.last_active_at)}</TableCell>
                          </TableRow>
                        ))}
                        {users && users.items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No activity in this range.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    {users?.total ?? 0} user(s) total in range.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Feature Usage
                  </Typography>
                  {features && features.items.length > 0 ? (
                    <Box sx={{ height: 240, mb: 2 }}>
                      <Bar
                        data={featureChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No feature views in this range.
                    </Typography>
                  )}
                  <TableContainer sx={{ maxHeight: 220 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Feature</TableCell>
                          <TableCell align="right">Views</TableCell>
                          <TableCell align="right">Unique Users</TableCell>
                          <TableCell align="right">Adoption</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(features?.items ?? []).map((row) => (
                          <TableRow key={row.event_name}>
                            <TableCell>{row.feature_name}</TableCell>
                            <TableCell align="right">{row.total_events}</TableCell>
                            <TableCell align="right">{row.unique_users}</TableCell>
                            <TableCell align="right">
                              <Chip size="small" label={`${row.adoption_percent.toFixed(1)}%`} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Sessions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Active
                      </Typography>
                      <Typography variant="h6">{sessions?.active_sessions ?? "—"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total
                      </Typography>
                      <Typography variant="h6">{sessions?.total_sessions ?? "—"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Avg Duration
                      </Typography>
                      <Typography variant="h6">{formatDuration(sessions?.avg_duration_seconds)}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    API Health
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Requests
                      </Typography>
                      <Typography variant="h6">{apiHealth?.total_requests ?? "—"}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Error Rate
                      </Typography>
                      <Typography variant="h6" color={apiHealth && apiHealth.error_rate_percent > 5 ? "error" : "inherit"}>
                        {apiHealth ? `${apiHealth.error_rate_percent.toFixed(2)}%` : "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Avg Response
                      </Typography>
                      <Typography variant="h6">
                        {apiHealth?.avg_response_time_ms != null ? `${Math.round(apiHealth.avg_response_time_ms)}ms` : "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        p95 Response
                      </Typography>
                      <Typography variant="h6">
                        {apiHealth?.p95_response_time_ms != null ? `${Math.round(apiHealth.p95_response_time_ms)}ms` : "—"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <TableContainer sx={{ maxHeight: 220 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Endpoint</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell align="right">Avg (ms)</TableCell>
                          <TableCell align="right">Requests</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(apiHealth?.slowest_endpoints ?? []).map((row, idx) => (
                          <TableRow key={`${row.endpoint}-${row.method}-${idx}`}>
                            <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{row.endpoint}</TableCell>
                            <TableCell>{row.method}</TableCell>
                            <TableCell align="right">{Math.round(row.avg_response_time_ms)}</TableCell>
                            <TableCell align="right">{row.request_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Events Breakdown
                  </Typography>
                  <TableContainer sx={{ maxHeight: 260 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Event</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(events?.items ?? [])
                          .slice()
                          .sort((a, b) => b.count - a.count)
                          .map((row) => (
                            <TableRow key={`${row.event_name}-${row.event_category}`}>
                              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{row.event_name}</TableCell>
                              <TableCell>
                                <Chip size="small" variant="outlined" label={row.event_category} />
                              </TableCell>
                              <TableCell align="right">{row.count}</TableCell>
                            </TableRow>
                          ))}
                        {events && events.items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No events in this range.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    {events?.total_events ?? 0} event(s) total in range.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

export default AnalyticsInsightsPage;
